import { Component, computed, effect, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatListModule } from '@angular/material/list';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  AddressModel,
  DefaultAddress,
  getAddressModelFromAzureMapAddressAndPosition,
} from '@models/module/fields/main';
import { DisplayType } from '@models/module/fields/enums';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslateModule } from '@ngx-translate/core';
import { AzureMapComponent } from '@shared/components/azure-map/azure-map.component';
import { AzureMapPosition } from '@shared/components/azure-map/azure-map.model';
import { AzureMapService } from '@shared/components/azure-map/azure-map.service';
import { NotFoundComponent } from '@system/not-found/not-found.component';
import { ConfirmationDialog, CipoDialogHeaderComponent } from '@shared/dialogs';
import { CipoAddressFormComponent } from '../cipo-address-form/cipo-address-form.component';
import { CipoAddressService, SyncGlobalAddressesRequest } from '../cipo-address.service';
import { FormsModule } from '@angular/forms';
import { Permission } from '@models/permission';
import { RouteCodeEnum } from '@models/menu';
import { UserStore } from '@signal-store/index';

export type CipoGlobalAddressesDialogData = {
  globalAddresses: AddressModel[];
};

@Component({
  selector: 'cipo-global-addresses-dialog',
  templateUrl: './cipo-global-addresses-dialog.component.html',
  styleUrls: ['./cipo-global-addresses-dialog.component.scss'],
  imports: [
    FormsModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    TranslateModule,
    MatDialogModule,
    MatButtonModule,
    MatDividerModule,
    MatListModule,
    MatTooltipModule,
    FlexLayoutModule,
    CipoAddressFormComponent,
    NotFoundComponent,
    AzureMapComponent,
    CipoDialogHeaderComponent,
  ],
})
export class CipoGlobalAddressesDialogComponent {
  readonly dialogRef = inject<MatDialogRef<CipoGlobalAddressesDialogComponent>>(MatDialogRef);
  private dialog = inject(MatDialog);
  private dialogData = inject<CipoGlobalAddressesDialogData>(MAT_DIALOG_DATA);
  azureMapService = inject(AzureMapService);
  addressService = inject(CipoAddressService);
  private userStore = inject(UserStore);

  private listsModule = computed(() => this.userStore.getModuleByCode(RouteCodeEnum.ListsManagement));

  /** True when the current user has Create permission on the Lists Management module. */
  canCreateGlobalAddress = computed(() => !!this.listsModule()?.operations?.find(op => op.id === Permission.Create));

  /** True when the current user has Update permission on the Lists Management module. */
  canUpdateGlobalAddress = computed(() => !!this.listsModule()?.operations?.find(op => op.id === Permission.Update));

  /** True when the current user has Delete permission on the Lists Management module. */
  canDeleteGlobalAddress = computed(() => !!this.listsModule()?.operations?.find(op => op.id === Permission.Delete));

  /**
   * True when the user can edit the currently selected address:
   * new addresses (no locationId) require Create, existing ones require Update.
   */
  canEditCurrentAddress = computed(() => {
    const current = this.addresses()[this.addressIndex()];
    const isNew = !current?.locationId;
    return isNew ? this.canCreateGlobalAddress() : this.canUpdateGlobalAddress();
  });

  /** Working copy of global addresses — all edits happen here before save. */
  addresses = signal<AddressModel[]>(this.dialogData.globalAddresses.map(a => ({ ...a })));
  /** Ids of addresses that were already persisted and have been removed in this session. */
  deletedLocationIds = signal<number[]>([]);
  changed = signal<boolean>(false);

  private initialized = false;
  changedEffect = effect(() => {
    // track both signals that cover every mutation: address edits/adds and deletions
    this.addresses();
    this.deletedLocationIds();
    if (!this.initialized) {
      this.initialized = true;
      return;
    }
    this.changed.set(true);
  });
  address = signal<AddressModel>(this.dialogData.globalAddresses[0] ?? { ...DefaultAddress });
  addressIndex = signal<number>(0);
  searchTerm = signal<string>('');
  readonly displayType = DisplayType.Address;

  coordinates = computed<AzureMapPosition[]>(() => this.addresses().map(addr => ({ lat: addr.lat, lon: addr.lon })));

  /** Addresses filtered by the search term for display in the list. */
  filteredAddresses = computed<AddressModel[]>(() => {
    const term = this.searchTerm().toLowerCase().trim();
    if (!term) return this.addresses();
    return this.addresses().filter(
      a => a.locationName?.toLowerCase().includes(term) || a.address?.toLowerCase().includes(term),
    );
  });

  isValid = computed(() => this.addresses().every(a => this.addressService.addressIsValid(a)));
  currentAddressIsValid = computed(
    () => !this.addresses().length || this.addressService.addressIsValid(this.addresses()[this.addressIndex()]),
  );

  addressIndexChangedEffect = effect(() => {
    this.address.set(this.addresses()[this.addressIndex()]);
  });

  addAddress(): void {
    if (!this.currentAddressIsValid()) {
      return;
    }
    this.addresses.update(current => [...current, { ...DefaultAddress }]);
    this.addressIndex.set(this.addresses().length - 1);
  }

  setAddress(index: number): void {
    if (!this.currentAddressIsValid()) {
      return;
    }
    // resolve filtered index back to real index when search is active
    const filtered = this.filteredAddresses();
    const realIndex = this.addresses().indexOf(filtered[index]);
    this.addressIndex.set(realIndex >= 0 ? realIndex : index);
  }

  deleteAddress(filteredIndex: number): void {
    const filtered = this.filteredAddresses();
    const target = filtered[filteredIndex];
    const realIndex = this.addresses().indexOf(target);
    if (realIndex < 0) {
      return;
    }
    const confirmRef = this.dialog.open(ConfirmationDialog, {
      data: {
        title: 'MODULE_DEF_ADDRESS.DELETE_GLOBAL_TITLE',
        messages: ['MODULE_DEF_ADDRESS.DELETE_GLOBAL_MESSAGE'],
      },
    });
    confirmRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }
      // track id for deletion on save only if it was already persisted
      if (target.locationId) {
        this.deletedLocationIds.update(ids => [...ids, target.locationId]);
      }

      this.addresses.update(current => {
        const next = [...current];
        next.splice(realIndex, 1);
        return next;
      });

      const newIndex = realIndex >= this.addresses().length ? this.addresses().length - 1 : realIndex;
      this.addressIndex.set(Math.max(0, newIndex));
      this.address.set(this.addresses()[this.addressIndex()] ?? { ...DefaultAddress });
    });
  }

  addressChanged(updatedAddress: AddressModel): void {
    if (!this.canEditCurrentAddress()) {
      // guard by create or update permission
      return;
    }

    this.addresses.update(current => current.map((a, i) => (i === this.addressIndex() ? updatedAddress : a)));

    if (this.address().lat !== updatedAddress.lat || this.address().lon !== updatedAddress.lon) {
      this.handleCoordinateChanged(updatedAddress);
    }

    this.address.set(updatedAddress);
  }

  handleCoordinateChanged(position: AzureMapPosition): void {
    const { lat, lon } = position;
    if (!lat || !lon) {
      return;
    }
    this.azureMapService.getAddressFromCoordinates(lat, lon).subscribe({
      next: addressResult => {
        if (!addressResult?.addresses?.length || !addressResult.addresses[0].address) {
          return;
        }
        const { address } = addressResult.addresses[0];
        const updated = {
          ...getAddressModelFromAzureMapAddressAndPosition(address, position),
          locationId: this.address()?.locationId,
          locationName: this.address()?.locationName,
          isGlobal: true,
        };
        this.address.set(updated);
        this.addresses.update(current => current.map((a, i) => (i === this.addressIndex() ? updated : a)));
      },
      error: () => {
        const updated = { ...this.address(), lat, lon };
        this.address.set(updated);
        this.addresses.update(current => current.map((a, i) => (i === this.addressIndex() ? updated : a)));
      },
    });
  }

  handleCoordinatesChanged(coordinates: AzureMapPosition[]): void {
    if (!this.canEditCurrentAddress()) {
      // guard by create or update permission
      return;
    }
    this.handleCoordinateChanged(coordinates[this.addressIndex()]);
  }

  save(): void {
    const request: SyncGlobalAddressesRequest = {
      upserted: this.addresses().map(a => ({ ...a, isGlobal: true })),
      deletedLocationIds: this.deletedLocationIds(),
    };
    this.addressService.syncGlobalAddresses(request).subscribe(refreshed => {
      this.dialogRef.close(refreshed);
    });
  }
}
